import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChartBar, Boat, Clock, TrendUp, Users } from "@phosphor-icons/react";
import { fmtDuration } from "../lib/duration";
import AnalyticsSpc from "../components/AnalyticsSpc";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export default function Analytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/analytics/summary");
                setData(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen">
            <Header />
            <main className="max-w-[1400px] mx-auto px-5 py-8">
                <div className="mb-6">
                    <div className="uppercase-label text-[var(--grindrod-red)]">Insights</div>
                    <h1 className="font-display font-black text-3xl text-[var(--grindrod-navy)]">
                        Analytics · Richards Bay Terminal
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Cross-vessel performance, delay patterns, client-level output — plus SPC / Six-Sigma analytics per operation.
                    </p>
                </div>

                {loading ? (
                    <div className="text-slate-500" data-testid="analytics-loading">Loading…</div>
                ) : !data ? null : (
                    <>
                        {/* KPI row */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <Kpi icon={<Boat size={18} />} label="Vessels Tracked" value={data.totals.vessels_total} sub={`${data.totals.vessels_active} active`} />
                            <Kpi icon={<ChartBar size={18} />} label="Reports Submitted" value={data.totals.reports_submitted} />
                            <Kpi icon={<TrendUp size={18} />} label="Avg Flow Rate" value={`${Math.round(data.totals.overall_avg_rate_tph)} t/h`} sub="weighted avg" />
                            <Kpi icon={<Clock size={18} />} label="Total Delay" value={fmtDuration(data.totals.total_delay_minutes)} sub={`${(data.totals.total_tons_loaded || 0).toLocaleString()} tons loaded`} />
                        </div>

                        {/* Top delay reasons + client output */}
                        <div className="grid md:grid-cols-2 gap-5 mb-6">
                            <div className="g-card">
                                <div className="g-card-header-red">
                                    <Clock size={18} />
                                    <div className="font-display font-bold uppercase tracking-wide">Top Delay Reasons</div>
                                </div>
                                {data.top_delay_reasons.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-slate-400" data-testid="no-delay-reasons">No delays recorded.</div>
                                ) : (
                                    <div className="p-4" style={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={data.top_delay_reasons} layout="vertical" margin={{ left: 10 }}>
                                                <CartesianGrid stroke="#EEF1F5" />
                                                <XAxis type="number" stroke="#64748B" fontSize={11} />
                                                <YAxis dataKey="reason" type="category" stroke="#64748B" fontSize={11} width={160} />
                                                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1" }} />
                                                <Bar dataKey="total_minutes" fill="#E3000F" name="Minutes" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>

                            <div className="g-card">
                                <div className="g-card-header-dark">
                                    <Users size={18} />
                                    <div className="font-display font-bold uppercase tracking-wide">Output by Client</div>
                                </div>
                                {data.by_client.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-slate-400" data-testid="no-clients">No completed vessels yet.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="g-table" data-testid="client-table">
                                            <thead>
                                                <tr>
                                                    <th>Client</th>
                                                    <th className="text-right pr-4">Vessels</th>
                                                    <th className="text-right pr-4">Tons delivered</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.by_client.map((c) => (
                                                    <tr key={c.client_name}>
                                                        <td className="font-semibold text-[var(--grindrod-navy)]">{c.client_name}</td>
                                                        <td className="tabular text-right pr-4">{c.vessels}</td>
                                                        <td className="tabular text-right pr-4">{Math.round(c.tons).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* SPC / Six-Sigma per-operation panel */}
                        <AnalyticsSpc />
                    </>
                )}
            </main>
        </div>
    );
}

function Kpi({ icon, label, value, sub }) {
    return (
        <div className="g-card p-4" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center gap-2 text-slate-500">
                {icon}
                <div className="uppercase-label text-[9px]">{label}</div>
            </div>
            <div className="metric-value tabular text-2xl text-[var(--grindrod-navy)] mt-1">{value}</div>
            {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
        </div>
    );
}
